import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useUploadInventoryItemsCsv,
  useListInventoryItemImports,
  getInventoryItemImport,
  getListInventoryItemImportsQueryKey,
  getListInventoryItemsQueryKey,
  type InventoryItemImport,
  type InventoryItemImportRowError,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, Download, FileSpreadsheet, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const TEMPLATE_HEADERS = [
  "name",
  "categoryName",
  "brand",
  "itemCode",
  "brandAgnosticSpecs",
  "hsnSacCode",
  "unitOfMeasurement",
  "packSize",
  "itemKind",
  "chargingModel",
  "stockInFrequency",
  "stockOutFrequency",
  "mrp",
  "sellingPrice",
  "memberDiscountPrice",
  "gstRateOnSale",
  "paymentCollection",
  "requiresPosEntry",
  "overageRate",
  "inventoryTracked",
  "currentStock",
  "storageLocation",
  "shelfLifeDays",
  "batchTracked",
  "reorderLevel",
  "reorderQuantity",
  "safetyStock",
  "maxStockLevel",
  "orderFrequencyDays",
  "standardOrderQty",
  "dailyQuantity",
  "status",
  "notes",
];

const TEMPLATE_SAMPLE_ROW = [
  "Nescafe Classic 50g",        // name
  "Pantry",                      // categoryName (must already exist)
  "Nescafe",                     // brand
  "",                            // itemCode (blank = auto-gen)
  "Instant coffee, jar",         // brandAgnosticSpecs
  "21011200",                    // hsnSacCode
  "piece",                       // unitOfMeasurement
  "",                            // packSize
  "Product",                     // itemKind
  "Pay_Per_Use",                 // chargingModel
  "Monthly",                     // stockInFrequency
  "Daily",                       // stockOutFrequency
  "199",                         // mrp
  "189",                         // sellingPrice
  "",                            // memberDiscountPrice
  "18",                          // gstRateOnSale
  "immediate",                   // paymentCollection
  "false",                       // requiresPosEntry
  "",                            // overageRate
  "true",                        // inventoryTracked
  "12",                          // currentStock
  "Pantry",                      // storageLocation
  "365",                         // shelfLifeDays
  "false",                       // batchTracked
  "4",                           // reorderLevel
  "12",                          // reorderQuantity
  "2",                           // safetyStock
  "24",                          // maxStockLevel
  "30",                          // orderFrequencyDays
  "12",                          // standardOrderQty
  "",                            // dailyQuantity
  "Active",                      // status
  "",                            // notes
];

function escapeCsvField(v: string | number): string {
  const s = String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function rowToCsv(cells: string[]): string {
  return cells.map(escapeCsvField).join(",");
}

const SAMPLE_TEMPLATE =
  rowToCsv(TEMPLATE_HEADERS) + "\n" + rowToCsv(TEMPLATE_SAMPLE_ROW) + "\n";

function downloadFile(filename: string, content: string, mime = "text/csv") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function fetchImportErrors(id: number): Promise<InventoryItemImportRowError[]> {
  const data = await getInventoryItemImport(id);
  return data.errors ?? [];
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ImportItemsDialog({ open, onOpenChange }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const { data: imports, isLoading } = useListInventoryItemImports({
    query: {
      queryKey: getListInventoryItemImportsQueryKey(),
      enabled: open,
    },
  });

  const uploadMutation = useUploadInventoryItemsCsv({
    mutation: {
      onSuccess: (result) => {
        toast({
          title: "Import processed",
          description: `${result.succeededRows} succeeded, ${result.failedRows} failed (of ${result.totalRows} rows)`,
        });
        void queryClient.invalidateQueries({ queryKey: getListInventoryItemImportsQueryKey() });
        void queryClient.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : "Upload failed";
        toast({ title: "Upload failed", description: msg, variant: "destructive" });
      },
    },
  });

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 5 MB", variant: "destructive" });
      return;
    }
    const text = await file.text();
    uploadMutation.mutate({ data: { filename: file.name, csv: text } });
  };

  const downloadErrors = async (job: InventoryItemImport) => {
    if (job.failedRows === 0) return;
    setDownloadingId(job.id);
    try {
      const errors = await fetchImportErrors(job.id);
      const lines = ["row,message", ...errors.map((er) => `${er.row},${escapeCsvField(er.message)}`)];
      const safeName = job.filename.replace(/\.csv$/i, "");
      downloadFile(`${safeName}-errors-${job.id}.csv`, lines.join("\n"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not download errors";
      toast({ title: "Download failed", description: msg, variant: "destructive" });
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadTemplate = () => {
    downloadFile("inventory-items-import-template.csv", SAMPLE_TEMPLATE);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" /> Import Items from CSV
          </DialogTitle>
          <DialogDescription>
            Bulk-create inventory items by uploading a CSV file. Download the template below
            to see the expected column layout.
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-sm space-y-1">
            <div>
              <strong>Required columns:</strong> name, categoryName, brandAgnosticSpecs, hsnSacCode,
              unitOfMeasurement, chargingModel, stockInFrequency, stockOutFrequency, mrp.
            </div>
            <div>
              <strong>Categories</strong> must already exist in this space — create them first under
              the Categories tab. Leave <code>itemCode</code> blank to auto-generate (ITM-NNNN).
            </div>
            <div>
              Photos, recipes, variants, and per-plan quotas are not part of the bulk import —
              edit imported items individually to add them.
            </div>
          </AlertDescription>
        </Alert>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <Download className="mr-2 h-4 w-4" /> Download template
          </Button>
          <Button size="sm" onClick={onPickFile} disabled={uploadMutation.isPending}>
            <Upload className="mr-2 h-4 w-4" />
            {uploadMutation.isPending ? "Uploading…" : "Upload CSV"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onFileChange}
            data-testid="input-items-csv-upload"
          />
        </div>

        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Uploader</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Succeeded</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead>Errors</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : !imports || imports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                    No imports yet. Upload a CSV to get started.
                  </TableCell>
                </TableRow>
              ) : (
                imports.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {new Date(job.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-medium truncate max-w-[220px]">{job.filename}</TableCell>
                    <TableCell className="text-sm">{job.uploadedByName}</TableCell>
                    <TableCell className="text-right tabular-nums">{job.totalRows}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <Badge variant="outline" className="bg-green-100 text-green-800 border-transparent">
                        {job.succeededRows}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {job.failedRows > 0 ? (
                        <Badge variant="outline" className="bg-red-100 text-red-800 border-transparent">
                          {job.failedRows}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {job.failedRows > 0 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadErrors(job)}
                          disabled={downloadingId === job.id}
                          data-testid={`button-download-item-errors-${job.id}`}
                        >
                          <Download className="mr-1 h-3 w-3" />
                          {downloadingId === job.id ? "Loading…" : "Error report"}
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
